'use client';

import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { TRANSLATIONS } from './i18n';

type StepperCommonProps = {
  className?: string;
  ariaLabel?: string;
};

export type ProgressStepperProgressProps = StepperCommonProps & {
  variant?: 'progress';
  value: number;
  durationMs?: number;
  trackClassName?: string;
  fillClassName?: string;
};

export type ProgressStepperStep = {
  id?: string | number;
  label?: ReactNode;
  title: ReactNode;
};

export type ProgressStepperTextProps = StepperCommonProps & {
  variant: 'text';
  steps: ProgressStepperStep[];
  activeStep?: number;
  stepClassName?: string;
  activeStepClassName?: string;
  inactiveStepClassName?: string;
  labelClassName?: string;
  titleClassName?: string;
};

export type ProgressStepperProps =
  | ProgressStepperProgressProps
  | ProgressStepperTextProps;

const clampProgressValue = (value: number) => Math.min(Math.max(value, 0), 100);

const getStepKey = (step: ProgressStepperStep, index: number) =>
  step.id ?? `step-${index}`;

export const ProgressStepper = (props: ProgressStepperProps) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  if (props.variant === 'text') {
    const {
      activeStep,
      ariaLabel = staticText.progress_steps,
      className,
      steps,
      stepClassName,
      activeStepClassName,
      inactiveStepClassName,
      labelClassName,
      titleClassName,
    } = props;

    return (
      <div
        aria-label={ariaLabel}
        className={classNames(
          'flex w-full items-start gap-spacing/gap/x',
          className,
        )}
      >
        {steps.map((step, index) => {
          const hasActiveState = activeStep !== undefined;
          const isReached = hasActiveState && index <= activeStep;

          return (
            <div
              key={getStepKey(step, index)}
              className={classNames(
                'flex min-w-0 flex-1 flex-col items-start gap-spacing/gap/soft-4px border-t-4 pt-spacing/gap/soft-12px',
                hasActiveState
                  ? isReached
                    ? 'border-fill/primary'
                    : 'border-fill/primary-light'
                  : 'border-borders/default',
                !isReached && inactiveStepClassName,
                isReached && activeStepClassName,
                stepClassName,
              )}
            >
              <p
                className={classNames(
                  'm-0 max-w-full truncate font-font-weight/body-2 text-font-size/body-2 leading-line-height/body-2-relaxed text-text/primary',
                  labelClassName,
                )}
                title={typeof step.label === 'string' ? step.label : undefined}
              >
                {step.label ?? `${staticText.step} ${index + 1}`}
              </p>
              <p
                className={classNames(
                  'm-0 max-w-full truncate font-font-weight/breadcrumb text-font-size/breadcrumb leading-line-height/breadcrumb text-text/secondary-lighter',
                  titleClassName,
                )}
                title={typeof step.title === 'string' ? step.title : undefined}
              >
                {step.title}
              </p>
            </div>
          );
        })}
      </div>
    );
  }

  const {
    ariaLabel = staticText.progress,
    className,
    durationMs = 300,
    fillClassName,
    trackClassName,
    value,
  } = props;
  const clampedValue = clampProgressValue(value);
  const fillStyle: CSSProperties = {
    transitionDuration: `${durationMs}ms`,
    width: `${clampedValue}%`,
  };

  return (
    <div
      aria-label={ariaLabel}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={clampedValue}
      className={classNames('w-full', className)}
      role="progressbar"
    >
      <div
        className={classNames(
          'h-1 w-full overflow-hidden rounded-full bg-borders/default',
          trackClassName,
        )}
      >
        <div
          className={classNames(
            'h-full rounded-full bg-fill/secondary transition-all ease-in-out',
            fillClassName,
          )}
          style={fillStyle}
        />
      </div>
    </div>
  );
};
